import { Box, Divider, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LINK_EXTERNAL_ICON from '../../../../assets/svg/link-external-icon.svg';
import CustomDrawer from '../../../../components/Drawer/Drawer';
import { colors } from '../../../../interfaces/color.types';
import { DamagedItemStatus, OrderItemStatus } from '../../../../interfaces/index.types';
import { formatHourMonthDayYear } from '../../../../utils/handleDateTime';
import DrawerOrderItem from '../../../Dashboard/components/DrawerOrderItem/DrawerOrderItem';
import { IFood, IOrders } from '../../../Dashboard/order.types';
import { useStyles } from './styles';
import { useAuthContext } from '../../../../context/AuthContext/AuthContext';
import { LANG_TEXT } from '../../../../constants/language';

function TableBodyCustom({ listOrder, rowsPerPage, page, showForSuperAdmin }: { listOrder: IOrders[]; rowsPerPage: number; page: number; showForSuperAdmin?: boolean }) {
  const classes = useStyles();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrders>();
  const [selectedFoodId, setSelectedFoodId] = useState<string | undefined>(undefined);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | undefined>(undefined);
  const { t } = useTranslation();
  const { userInfo } = useAuthContext();

  const restaurantMap = useMemo(() => {
    const map: Record<string, string> = {};
    (userInfo?.restaurants || []).forEach((r: any) => {
      map[r.id] = r.name;
    });
    return map;
  }, [userInfo]);

  const requestedDeliveryDisplayMap = useMemo(() => {
    return listOrder.reduce<Record<string, string>>((acc, row) => {
      if (!row?.id) return acc;

      if (!row.requested_delivery_time) {
        acc[row.id] = '-';
        return acc;
      }

      const requestedDate = formatHourMonthDayYear(row.requested_delivery_time).date;

      acc[row.id] =
        row.delivery_request === 'WHEN_ITS_READY' ? `${t(LANG_TEXT.WHEN_ITS_READY)}` : row.delivery_request ? `${requestedDate} ${row.delivery_request}` : requestedDate;

      return acc;
    }, {});
  }, [listOrder, t]);

  const renderRestaurantNames = (row: IOrders): React.ReactNode => {
    let names: string[];
    if (row.multi_orders && row.multi_orders.length > 0) {
      names = row.multi_orders.map((o) => restaurantMap[o.restaurant_id]).filter(Boolean) as string[];
      if (names.length === 0) names = ['-'];
    } else {
      names = [restaurantMap[row.restaurant_id] || '-'];
    }
    return (
      <>
        {names.map((name, index) => (
          <React.Fragment key={index}>
            <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'} sx={{ padding: '16px 0' }}>
              {name}
            </Typography>
            {index !== names.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
      </>
    );
  };

  const toggleDrawer = (open: boolean) => {
    setOpenDrawer(open);
  };

  const multiOrderIndicator = (row: IOrders) => {
    if (!row.multi_orders?.length) return '-';

    const doneOrders = row.multi_orders.filter(
      (order) => !([OrderItemStatus.PREPARING, OrderItemStatus.DAMAGED_PREPARING, DamagedItemStatus.DAMAGED_CREATED] as string[]).includes(order.status)
    );

    return `${doneOrders.length}/${row.multi_orders.length}`;
  };

  return (
    <TableBody>
      {listOrder.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: IOrders, key: number) => {
        const backgroundColor = row.is_pre_order ? '#FFF4ED' : 'rgba(255, 255, 255, 1)';

        return (
          <TableRow
            sx={{
              backgroundColor: backgroundColor,
            }}
            hover
            role="checkbox"
            tabIndex={-1}
            key={`${row.id}-${key}`}
          >
            <TableCell key={0} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {formatHourMonthDayYear(row.order_create_time).date}
              </Typography>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {formatHourMonthDayYear(row.order_create_time).time}
              </Typography>
            </TableCell>
            <TableCell key={1} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.id}
              </Typography>
            </TableCell>
            {showForSuperAdmin && (
              <TableCell key={'restaurant_name'} sx={{ padding: 0 }} className={classes.TableCell}>
                {renderRestaurantNames(row)}
              </TableCell>
            )}
            <TableCell key={2} sx={{ padding: 0 }} className={classes.TableCell}>
              {row.type === 'promotion'
                ? row.promotion_foods &&
                  row.promotion_foods.map((item: IFood, index: number) => (
                    <React.Fragment key={index}>
                      <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'} sx={{ padding: '16px 0' }}>
                        {item.item_name}
                      </Typography>
                      {index !== (row.promotion_foods || []).length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))
                : row.foods.map((item: IFood, index: number) => (
                    <React.Fragment key={index}>
                      <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'} sx={{ padding: '16px 0' }}>
                        {item.item_name}
                      </Typography>
                      {index !== row.foods.length - 1 && <Divider sx={{ my: 1 }} />}
                    </React.Fragment>
                  ))}
            </TableCell>
            <TableCell key={3} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.seat_number}
              </Typography>
            </TableCell>
            <TableCell key={4} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {row.id ? requestedDeliveryDisplayMap[row.id] : '-'}
              </Typography>
            </TableCell>
            <TableCell key={5} className={classes.TableCell}>
              <Typography fontSize={14} lineHeight={'20px'} color={'var(--Gray-600, #475467)'}>
                {multiOrderIndicator(row)}
              </Typography>
            </TableCell>
            <TableCell key={6} className={classes.TableCell}>
              <Box
                sx={{
                  background: colors.find((color) => color.status === row.order_status)?.bgrColor || 'none',
                }}
                className={classes.BoxStatusItem}
              >
                <Typography
                  sx={{
                    color: colors.find((color) => color.status === row.order_status)?.color || 'none',
                  }}
                  fontSize={12}
                  lineHeight={'18px'}
                  fontWeight={500}
                  textAlign={'center'}
                >
                  {t(colors.find((color) => color.status === row.order_status)?.name || row.id)}
                </Typography>
              </Box>
            </TableCell>
            <TableCell key={7} sx={{ borderBottom: '1px solid #e0e0e0' }}>
              {showForSuperAdmin ? (
                <Box sx={{ cursor: 'pointer' }}>
                  <Box sx={{ mt: 1 }}>
                    {row.type === 'promotion'
                      ? row.promotion_foods &&
                        row.promotion_foods.map((item: IFood, index: number) => (
                          <React.Fragment key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
                              <Box
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedOrder(row);
                                  setSelectedFoodId(item.item_food_id);
                                  setSelectedRestaurantId(item.restaurantId);
                                  toggleDrawer(true);
                                }}
                              >
                                <img src={LINK_EXTERNAL_ICON} height={20} width={20} alt="LINK_EXTERNAL_ICON" />
                              </Box>
                            </Box>
                            {index !== (row.promotion_foods || []).length - 1 && <Divider sx={{ my: 1 }} />}
                          </React.Fragment>
                        ))
                      : row.foods.map((item: IFood, index: number) => (
                          <React.Fragment key={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}>
                              <Box
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedOrder(row);
                                  setSelectedFoodId(item.item_food_id);
                                  setSelectedRestaurantId(item.restaurantId);
                                  toggleDrawer(true);
                                }}
                              >
                                <img src={LINK_EXTERNAL_ICON} height={20} width={20} alt="LINK_EXTERNAL_ICON" />
                              </Box>
                            </Box>
                            {index !== row.foods.length - 1 && <Divider sx={{ my: 1 }} />}
                          </React.Fragment>
                        ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedOrder(row);
                    setSelectedFoodId(undefined);
                    setSelectedRestaurantId(undefined);
                    toggleDrawer(true);
                  }}
                >
                  <img src={LINK_EXTERNAL_ICON} height={20} width={20} alt="LINK_EXTERNAL_ICON" />
                </Box>
              )}
            </TableCell>
          </TableRow>
        );
      })}
      {openDrawer && (
        <CustomDrawer
          openDrawer={openDrawer}
          toggleDrawer={toggleDrawer}
          DrawerItem={<DrawerOrderItem order={selectedOrder} toggleDrawer={toggleDrawer} selectedFoodId={selectedFoodId} selectedRestaurantId={selectedRestaurantId} />}
        />
      )}
    </TableBody>
  );
}

export default TableBodyCustom;
